import React, { useEffect, useRef, useState } from "react";
import Lightbox from "../gallery/Lightbox";

const imgUrl = (img, size) =>
  `${process.env.REACT_APP_API_URL}${(size && img.formats?.[size]?.url) || img.url}`;

/**
 * ดูภาพอินโฟกราฟิกแบบสไลด์ — ภาพใหญ่ตรงกลาง เลื่อนด้วยปุ่มซ้าย/ขวาหรือปัดบนมือถือ
 * แถวภาพย่อด้านล่างกดข้ามไปภาพไหนก็ได้ กดภาพใหญ่เพื่อดูเต็มจอ (Lightbox)
 * images: ไฟล์ media จาก Strapi ตามลำดับที่เรียงใน Image (ภาพแรก = หน้าปก)
 */
export default function InfographicViewer({ images, title, lang }) {
  const [index, setIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touchX = useRef(null);
  const thumbsRef = useRef(null);

  const total = images.length;
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  // ให้ภาพย่อที่เลือกอยู่เลื่อนมาอยู่ในจอเสมอ
  useEffect(() => {
    const el = thumbsRef.current?.children[index];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [index]);

  if (total === 0) return null;

  const current = images[index];
  const navBtn =
    "absolute top-1/2 -translate-y-1/2 w-[44px] h-[44px] rounded-full bg-white/90 hover:bg-white shadow-[0_2px_10px_rgba(0,0,0,0.2)] text-slateGray text-title leading-none flex items-center justify-center transition-colors";

  return (
    <div className="max-w-[760px] mx-auto">
      {/* ภาพหลัก */}
      <div
        className="relative bg-lightBg rounded-[12px] overflow-hidden select-none"
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
        }}
      >
        <img
          key={current.id || index}
          src={imgUrl(current, "large")}
          alt={title || ""}
          className="w-full max-h-[80vh] object-contain cursor-zoom-in"
          onClick={() => setZoomOpen(true)}
          draggable={false}
        />

        {total > 1 && (
          <>
            <p className="absolute top-[12px] right-[12px] px-[10px] py-[2px] rounded-full bg-black/60 text-white text-small">
              {index + 1} / {total}
            </p>
            <button
              type="button"
              aria-label={lang === "en" ? "Previous" : "ภาพก่อนหน้า"}
              className={`${navBtn} left-[12px]`}
              onClick={prev}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label={lang === "en" ? "Next" : "ภาพถัดไป"}
              className={`${navBtn} right-[12px]`}
              onClick={next}
            >
              ›
            </button>
          </>
        )}
      </div>

      <p className="text-small text-metaGray text-center mt-[8px]">
        {lang === "en" ? "Tap the image to view full size" : "กดที่ภาพเพื่อดูขนาดเต็ม"}
      </p>

      {/* แถวภาพย่อ */}
      {total > 1 && (
        <div ref={thumbsRef} className="flex gap-[10px] overflow-x-auto mt-[16px] pb-[6px] px-[2px] pt-[2px]">
          {images.map((img, i) => (
            <button
              key={img.id || i}
              type="button"
              aria-label={`${lang === "en" ? "Image" : "ภาพที่"} ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`shrink-0 w-[84px] aspect-[4/5] rounded-[8px] overflow-hidden bg-lightGray transition-all ${
                i === index ? "ring-[3px] ring-brand" : "opacity-60 hover:opacity-100"
              }`}
            >
              <img
                src={imgUrl(img, "thumbnail")}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}

      {zoomOpen && (
        <Lightbox
          images={images}
          index={index}
          onChange={setIndex}
          onClose={() => setZoomOpen(false)}
          lang={lang}
        />
      )}
    </div>
  );
}
